import { JumpConfig } from "@/lib/jumpPage";

/**
 * 团购商卡类型声明（接口下发的结构）
 */
export interface ProductCard {
  productId: string;
  title: string;
  shortTitle: string;
  headImage: string;
  squareImage: string;
  scpSource: ScpSourceEnum;
  longProductId: number;
  canBuy: boolean;
  headImageList: string[];
  skuInfo: SkuInfo;
  processBar: ProcessBar;
  priceVO: PriceVO;
  promotionVO: PromotionVO;
  rankInfo: RankInfo;
  tagInfo: TagInfoDetail;
  stockInfo: StockInfo;
  productTimeInfo: ProductTimeInfo;
  rewardInfo: RewardInfo;
  poiInfo: PoiInfo;
  reviewInfo: ReviewInfo;
  eventTrackingInfo: EventTrackingInfo;
}

export interface SkuInfo {
  skuId: string;
  skuName: string;
  encryptedSkuId: string | null;
}

export interface ProcessBar {
  processType: string | null;
  text: string;
  percent: number;
}

export interface PriceVO {
  salePrice: string;
  originPrice: string;
  qqFinalPrice: string;
  qqSingleFinalPrice: string | null;
  mtFinalPrice: string | null;
  discount: string;
  qqDiscount: string;
  qqReduceAmount: string | null;
  reduceAmount: string | null;
  superReduceAmount: string;
  promotionType: string | null;
  isMultiPrice: boolean | null;
  magicalMemberCouponAmount: string | null;
  afterCashbackPrice: string | null;
  afterCashbackDiscount: string | null;
  afterCashbackSuperReduceAmount: string | null;
  priceDesc: string;
}

export interface PromotionVO {
  promotionPriceTextType: string | null;
  promotionPriceTextName: string | null;
  promotionAmount: string | null;
  activityId: string | null;
  activityType: string | null;
  buyButton: string | null;
  couponBatchId: string | null;
  title: string | null;
  endTime: number | null;
  expireDays: number | null;
  receivedStatus: number | null;
  canUseQqDiscount: boolean;
  promotionType: number;
  promotionEndTime: number;
}

export interface RankInfo {
  comprehensiveRank: string | null;
  productRank: string | null;
  poiRank: string | null;
}

export interface TagInfoDetail {
  topSaleHotTag: string | null;
  limitCoupon: boolean;
  dpStarTag: string | null;
  iconTags: string | null;
  isSuperReduce: boolean;
  isNew: boolean;
  limitHighReward: boolean;
  sellingPoint: string | null;
  deliverable: boolean | null;
  videoStatus: boolean | null;
  poiNumTag: string;
  skuContentTag: string | null;
  superReduceProduct: string | null;
  channel: string | null;
  lowPriceTag: string | null;
  qqExclusivePrice: string | null;
  userGoodPrice: string | null;
  isTop: boolean;
  pictureMode: string | null;
  popularBrand: boolean;
  magicalMemberCouponType: number;
  recommendReasonList: string | null;
  assembleField: AssembleFieldLabelVO;
  groupSavingTag: string | null;
}

export interface AssembleFieldLabelVO {
  productInfoLabel: ProductInfoLabel[];
  promotionLabel: PromotionLabel[];
  userTraceLabel: UserTraceLabel[];
  pricePowerLabel: PricePowerLabel[];
  configurableUrlLabel: Label[];
}

export interface StockInfo {
  skuRemainStock: number;
  stockStatus: number;
  saleNumber: string | null;
  salesText: string;
  qqSaleNumber: string | null;
}

export interface ProductTimeInfo {
  countDownEndTime: string;
  endTime: string;
  promisingTime: string | null;
}

export interface RewardInfo {
  rewardAmount: string | null;
  baseRewardAmount: string | null;
}

export interface PoiInfo {
  poiId: string;
  distance: string;
  poiName: string;
  areaName: string;
  poiShortName: string;
  frontImg: string;
  poiAdvantageType: number;
  poiAdvantageDesc: string;
  poiBrandLogo: string;
  rankType: string | null;
  poiBrandName: string;
  poiIdEncrypt: string;
  distanceNew: string;
  poiNumber: number;
  sqsJumpUrl: string | null;
  poiDetailJumpUrl: string | null;
  poiDetailJumpAppId: string | null;
  dpShopUuid: string | null;
  dpShopUuidEncrypt: string | null;
}

export interface ReviewInfo {
  reviewList: (string | null)[];
  allCommentCountDesc: string | null;
  positiveRatioDesc: string | null;
}

export interface EventTrackingInfo {
  odpLaunchId: string | null;
}

/** ----- 标签类型声明 start ----- */
export interface BasicLabel {
  type: number;
  labelContent: string;
  labelItemMap?: Record<string, string>;
}

export enum ProductInfoLabelType {
  RANK = 1, // 榜单信息
  DIANPING_SCORE = 2, // 点评高分
  SALE_NUM = 3, // 已售销量
}

export enum PromotionLabelType {
  SECKILL = 5, // 限时秒杀
  CASH_BACK = 6, // 单单返
  SUBSIDY = 7, // 补贴
  MAGIC_COUPON = 8, // 可用神券
  AVAILABLE_COUPON = 16, // 最大可用券
  CUT_DOWN = 17, // 页面立减
}

export enum UserTraceLabelType {
  USER_TRACE = 4, // 你常买、你买过、你看过
}

export enum PricePowerLabelType {
  PERIOD_LOWEST_PRICE = 9, // 历史低价
  CHEAPER_IN_PLATFORM = 10, // 比站内便宜（比日常团购省）
  DISCOUNT = 11, // x折
  CUT_PERCENT = 12, // 降价x%
  CUT_DOWN = 13, // 直降x元
}

export enum ConfigurableUrlLabelType {
  TODAY_NEW_URL = 14, // 可配置链接
}

export interface ProductInfoLabel extends BasicLabel {
  type: ProductInfoLabelType;
}

export interface PromotionLabel extends BasicLabel {
  type: PromotionLabelType;
}

export interface UserTraceLabel extends BasicLabel {
  type: UserTraceLabelType;
}

export interface PricePowerLabel extends BasicLabel {
  type: PricePowerLabelType;
}

export interface AvailableCouponLabel {
  prefix: string;
  amount: string;
  suffix: string;
  wholeLabel: string; // 完整的文案
  type: PromotionLabelType.AVAILABLE_COUPON | PromotionLabelType.CUT_DOWN;
}

// 接口下发的标签种类
export type Label = ProductInfoLabel | PromotionLabel | UserTraceLabel | PricePowerLabel;

export interface ProductAggregationTag {
  productInfoLabel: ProductInfoLabel[],
  promotionLabel: PromotionLabel[],
  userTraceLabel: UserTraceLabel[],
  pricePowerLabel: PricePowerLabel[],
  configurableUrlLabel: Label[],
}
/** ----- 标签类型声明 end ----- */


export enum ScpSourceEnum {
  qqv1 = 10,
  meishi = 30,
  qqv2 = 20,
  daozong = 40, // 到综
  dzCommon = 41, // 到综标品
  ticket = 50, // 门票
  hotel = 60, // 酒店
  travel = 70, // 度假
  wmCoupon = 80, // 外卖神抢手券
  medical = 90, // 医药
  phf = 100, // 拼好饭
}

// 供给类型标签
export enum PRODUCT_CATEGORY_TYPE {
    WAIMAI = 1, // 外卖
    GROUP = 2, // 团购
    MEDICINE = 3, // 买药
}

// 前端处理后的价格力标签，增加一个 labelDiscount 字段
export interface EnhancedPriceLabel extends PricePowerLabel {
  type: PricePowerLabelType; // 标签类型
  labelContent: string; // 标签内容
  labelDiscount?: string | null; // 折扣信息，用于x天低价标签（type = 1）
}

// 处理后的商品卡片信息
export interface EnhancedProductCard {
  // 左侧图片、供给标签
  image: string; // 商品图片
  supplyTagImage?: string; // 图片左上方的供给标签
  categoryType: PRODUCT_CATEGORY_TYPE;
  areaName?: string; // 商圈名称
  distance: string | null; // 距离
  // deliveryTime: string | null; // 配送时间

  // 标题
  // beforeTitleTag?: string; // 标题标签图片
  brandName: string; // 品牌名称
  skuName: string; // 商品名称

  // 分类标签
  // 商品信息标签行（第一行标签）
  productInfoTagList?: Array<ProductInfoLabel>;
  // 营销标签行（第二行标签）
  marketingTagList?: Array<PromotionLabel | UserTraceLabel>;
  // 可用优惠券标签
  availableCouponTag?: AvailableCouponLabel | null;

  // 价格
  price: string;
  originPrice?: string; // 原价
  priceTag: EnhancedPriceLabel | null; // 比价标签/折扣标签
  wholeSaleNum?: number; // 多次卡，使用次数
  canBuy: boolean;
  // 跳转配置
  operation: JumpConfig;
  // 埋点上报需要
  poiId: string;
  productId: string;
  poiName: string;
  secondCategoryId: string;
}
